import { Message } from '@scale/shared/types/chat-completion';
import { z } from 'zod';

export interface ToolCall {
  functionName: string;
  functionParameters: Record<string, any>;
  functionMetadata: Record<string, any>;
}

export const BaseParamsSchema = z.object({});
export type BaseParams = z.infer<typeof BaseParamsSchema>;

export abstract class ToolCallStrategy {
  params: BaseParams;

  constructor(params: BaseParams) {
    this.params = params;
  }

  /*
   * Given a message containing client-side raw tool calls
   * Parse each tool call into a ToolCall object
   *
   * Return an empty array if no tool calls can be parsed
   */
  abstract parseToolCalls(message: Message): ToolCall[];

  /*
   * Given a message and a list of tool calls
   * 1. Transform the tool calls into the client-side format
   * 2. Transform the message to include the tool calls
   * 3. Return the new message and a stringified version of the output for the UI to display
   */
  abstract writeToolCalls(
    message: Message,
    toolCalls: ToolCall[],
  ): {
    message: Message;
    output: string;
  };

  /*
   * A function for pulling tool schemas that should be available to the user
   */
  abstract getToolSchemas(history?: Message[]): Record<string, z.ZodTypeAny>;

  /*
   * Create a blank ToolCall object
   */
  createBlankToolCall(): ToolCall {
    return {
      functionName: '',
      functionParameters: {},
      functionMetadata: {},
    };
  }

  /*
   * Get a list of tool ids that are available to the user
   */
  getToolIds() {
    return Object.keys(this.getToolSchemas());
  }

  /*
   * Get a zod schema for a tool by its id
   */
  getToolSchema(toolId: string): z.ZodTypeAny {
    return this.getToolSchemas()[toolId] || z.object({});
  }

  /*
   * Validate the parameters of a tool call.
   * 1. Validates that required parameters are present
   * 2. No additional parameters are present
   */
  validateToolCallParams(toolCall: ToolCall): boolean {
    const schema = this.getToolSchema(toolCall.functionName);
    const result = schema.safeParse(toolCall.functionParameters);
    if (result.success) {
      return true;
    }
    return result.success;
  }
}
